import type { ScaleLinear, ScaleSymLog, ScaleTime } from 'd3-scale';

import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import type { TimeseriesDatapoint } from '../../../../core/types/timeseries.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import {
  SeriesMetadata,
  WeakMapDatapoints,
} from '../../../types/timeseries.js';
import { getDatapointTime } from '../../../utils/timeseries.js';
import { getValueWithBoundaries } from '../shared/utils/clamp-value.js';

/**
 * Return boolean related if data-point is gap
 *
 * @param dataPoint - The data point
 * @param datapointsMap - Data points metadata map
 * @returns Whether the accessor is defined
 */

export const definedAccessor = (
  dataPoint: TimeseriesDatapoint,
  datapointsMap: WeakMapDatapoints,
) => !safeGet(datapointsMap, dataPoint).isGap;

/**
 * Get x-accessor base on xScale
 *
 * @param xScale - The x-axis scale
 * @param dataPointsMap -
 * @param clampedScale - the clamped scale
 * @returns The area chart x-accessor
 */
export const xAxisAccessor =
  (
    xScale: ScaleTime<number, number>,
    dataPointsMap: WeakMapDatapoints,
    clampedScale: ScaleTime<number, number>,
  ) =>
  (dataPoint: TimeseriesDatapoint) => {
    const { start, end, center } = safeGet(dataPointsMap, dataPoint);

    const timestamp = getDatapointTime(start, end, center);

    return getValueWithBoundaries(timestamp, xScale, clampedScale);
  };

/**
 * Get y0-accessor base on yScale
 *
 * @param yScale - The y-axis scale
 * @param dataPointsMap - Data points metadata map
 * @returns The area chart y0-accessor
 */
export const y0AxisAccessor =
  (
    yScale: ScaleSymLog<number, number> | ScaleLinear<number, number>,
    dataPointsMap: WeakMapDatapoints,
  ) =>
  (dataPoint: TimeseriesDatapoint) => {
    const dataPointMetadata = safeGet(dataPointsMap, dataPoint);
    const value =
      (dataPointMetadata.cumulativeValue ?? 0) - (dataPointMetadata.value ?? 0);

    return yScale(value);
  };

/**
 * Get y1-accessor base on yScale
 *
 * @param yScale - The y-axis scale
 * @param dataPointsMap - Data points metadata map
 * @returns The area chart y0-accessor
 */
export const y1AxisAccessor =
  (
    yScale: ScaleSymLog<number, number> | ScaleLinear<number, number>,
    dataPointsMap: WeakMapDatapoints,
  ) =>
  (dataPoint: TimeseriesDatapoint) => {
    const value = safeGet(dataPointsMap, dataPoint).cumulativeValue ?? 0;

    return yScale(value);
  };

/**
 * Get curry accessor of the y1-accessor
 *
 * @returns y1AxisAccessor
 */
export function y1(
  yScaleByUnit: (
    unit: ChartUnit,
  ) => ScaleSymLog<number, number> | ScaleLinear<number, number>,
  seriesMetadata: SeriesMetadata,
  datapointsMap: WeakMapDatapoints,
) {
  return (dataPoint: TimeseriesDatapoint) =>
    y1AxisAccessor(
      yScaleByUnit(seriesMetadata.unit),
      datapointsMap,
    )(dataPoint) ?? 0;
}

/**
 * Get curry accesor of the y0-accessor
 *
 * @returns y0AxisAccessor
 */
export function y0(
  yScaleByUnit: (
    unit: ChartUnit,
  ) => ScaleSymLog<number, number> | ScaleLinear<number, number>,
  seriesMetadata: SeriesMetadata,
  datapointsMap: WeakMapDatapoints,
) {
  return (dataPoint: TimeseriesDatapoint) =>
    y0AxisAccessor(
      yScaleByUnit(seriesMetadata.unit),
      datapointsMap,
    )(dataPoint) ?? 0;
}
